import {
  StyledDataGrid,
  useDataGridContext,
} from '@issuemanager/apps/client/components';
import {
  useAllQueries,
  useMonitorMasterIssuesList,
} from '@issuemanager/apps/client/hooks';
import { useIssues } from '@issuemanager/client/features/issues';
import { Stack } from '@mui/material';
import { GridEventListener, useGridApiRef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';
import { IssuesEmptyState } from '../emptyState';
import {
  makeActionsMasterIssueRowsModel,
  mapIssueGetToMasterIssueRowModel,
} from '../helper';
import { QuickFilters } from '../quick-filters';
import { Statistics } from '../statistics';
import { IssueGridRow } from './issue-row';
import { IssuesGridToolbar } from './issues-grid-toolbar';
import { MasterIssueRowModel } from './types';
import { useIssueGridColumns } from './use-issue-grid-columns';
import { useIssueGridQueryParams } from './use-issue-grid-query-params';

export function IssueGrid() {
  const [_expandedIssues, setExpandedIssues] = useState<string[]>([]);

  const apiRef = useGridApiRef();
  const dataGridContext = useDataGridContext();
  const queryParams = useIssueGridQueryParams(
    dataGridContext.filterModel,
    dataGridContext.sortModel,
    dataGridContext.columnVisibilityModel,
  );

  const {
    // excluded values from filter
    actionCreatedAtRangeStart: _actionCreatedAtRangeStart,
    actionCreatedAtRangeEnd: _actionCreatedAtRangeEnd,
    ...issueFilters
  } = queryParams.filter;
  const commonQueryParams = {
    page: dataGridContext.paginationModel.currentPage,
    limit: dataGridContext.paginationModel.pageSize,
    filter: {
      ...issueFilters,
      searchTerm: dataGridContext.searchTerm,
    },
    sort: queryParams.sort,
  };

  const issuesQuery = useIssues(commonQueryParams);

  const monitorMasterIssuesList = useMonitorMasterIssuesList();

  // mapping issue to issue-path for data-tree
  // TODO: Keep rows expanded after applying Issue filters
  const combinedRows = useMemo((): MasterIssueRowModel[] => {
    if (!issuesQuery.data?.data) return [];

    const issuesData = issuesQuery.data?.data;

    const rows = mapIssueGetToMasterIssueRowModel(issuesData);

    return rows.flatMap((issue, index) => {
      const issueRow: MasterIssueRowModel = { ...issue, path: [issue.code] };
      const actionsForIssue = makeActionsMasterIssueRowsModel(
        issuesData[index].actions,
        issuesData[index].code,
      );

      return [issueRow, ...actionsForIssue];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuesQuery.dataUpdatedAt]);

  useAllQueries({
    queries: [issuesQuery],
    onSuccess: () => {
      monitorMasterIssuesList.stop();
    },
    onError: () => {
      monitorMasterIssuesList.error();
    },
  });

  useEffect(() => {
    if (!apiRef.current) return undefined;

    const handleRowExpansionChange: GridEventListener<
      'rowExpansionChange'
    > = async (params) => {
      const groupingKey = String(params.groupingKey);
      if (params.childrenExpanded) {
        setExpandedIssues((prevState) => {
          if (prevState.includes(groupingKey)) {
            return prevState;
          }
          return [...prevState, groupingKey];
        });
      } else {
        setExpandedIssues((prevState) =>
          prevState.filter((issue) => issue != groupingKey),
        );
      }
    };

    return apiRef.current.subscribeEvent(
      'rowExpansionChange',
      handleRowExpansionChange,
    );
  }, []);

  useEffect(() => {
    if (!issuesQuery.data) return;
    dataGridContext.setPaginationModel((prevState) => ({
      ...prevState,
      total: issuesQuery.data.meta.itemCount,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuesQuery.dataUpdatedAt]);

  const { columns, groupingColDef } = useIssueGridColumns();

  return (
    <div data-testid='issue-grid'>
      <Stack spacing={4}>
        <Statistics />
        <QuickFilters />

        <StyledDataGrid
          apiRef={apiRef}
          error={issuesQuery.isError}
          components={{
            NoRowsOverlay: IssuesEmptyState,
            Toolbar: IssuesGridToolbar,
            Row: IssueGridRow,
          }}
          getRowId={(row) => row.path}
          loading={issuesQuery.isFetching}
          disableSelectionOnClick
          columns={columns}
          rows={combinedRows}
          defaultGroupingExpansionDepth={0}
          disableChildrenSorting
          treeData
          getTreeDataPath={(row) => row.path}
          groupingColDef={groupingColDef}
          initialState={{
            sorting: {
              sortModel: [
                {
                  sort: 'asc',
                  field: '__tree_data_group__',
                },
              ],
            },
          }}
        />
      </Stack>
    </div>
  );
}
